/**
 * Extension of the mdl-menu Component, to handle cascading submenus
 */
/**
     * Class constructor for dropdown MDL component.
     * Implements MDL component design pattern defined at:
     * https://github.com/jasonmayes/mdl-component-design-pattern
     *
     * @constructor
     * @param {HTMLElement} element The element that will be upgraded.
     */
var MaterialCascadingMenu = function MaterialCascadingMenu(element) {
    this.element_ = element;
    // Initialize instance.
    this.init();
};
window['MaterialCascadingMenu'] = MaterialCascadingMenu;
/**
     * Store constants in one place so they can be updated easily.
     *
     * @enum {string | number}
     * @private
     */
MaterialCascadingMenu.prototype.Constant_ = {
    // Total duration of the menu animation.
    TRANSITION_DURATION_SECONDS: 0.3,
    // The fraction of the total duration we want to use for menu item animations.
    TRANSITION_DURATION_FRACTION: 0.8,
    // How long the menu stays open after choosing an option (so the user can see
    // the ripple).
    CLOSE_TIMEOUT: 150
};
/**
     * Keycodes, for code readability.
     *
     * @enum {number}
     * @private
     */
MaterialCascadingMenu.prototype.Keycodes_ = {
    ENTER: 13,
    ESCAPE: 27,
    SPACE: 32,
    UP_ARROW: 38,
    DOWN_ARROW: 40
};
/**
     * Store strings for class names defined by this component that are used in
     * JavaScript. This allows us to simply change it in one place should we
     * decide to modify at a later date.
     *
     * @enum {string}
     * @private
     */
MaterialCascadingMenu.prototype.CssClasses_ = {
    CONTAINER: 'mdl-menu__container',
    OUTLINE: 'mdl-menu__outline',
    ITEM: 'mdl-menu__item',
    ITEM_RIPPLE_CONTAINER: 'mdl-menu__item-ripple-container',
    RIPPLE_EFFECT: 'mdl-js-ripple-effect',
    RIPPLE_IGNORE_EVENTS: 'mdl-js-ripple-effect--ignore-events',
    RIPPLE: 'mdl-ripple',
    // Statuses
    IS_UPGRADED: 'is-upgraded',
    IS_VISIBLE: 'is-visible',
    IS_ANIMATING: 'is-animating',
    // Alignment options
    BOTTOM_LEFT: 'mdl-menu--bottom-left',
    // This is the default.
    BOTTOM_RIGHT: 'mdl-menu--bottom-right',
    TOP_LEFT: 'mdl-menu--top-left',
    TOP_RIGHT: 'mdl-menu--top-right',
    UNALIGNED: 'mdl-menu--unaligned',
    // Additional classes for cascade
    OUTLINE_SUBMENU: 'mdl-menu__outline-submenu',
    SUBMENU: 'mdl-menu-cascade__open-sub',
    NOCLOSE: 'mdl-menu__item-noclose',
    MAX_HEIGHT: 'mdl-menu--window-height',
    MOVE_TO_BODY: 'mdl-menu-move-to-body'
};
/**
     * Initialize element.
     */
MaterialCascadingMenu.prototype.init = function () {
    if (this.element_) {
        // Create container for the menu.
        var container = document.createElement('div');
        container.classList.add(this.CssClasses_.CONTAINER);
        this.element_.parentElement.insertBefore(container, this.element_);
        this.element_.parentElement.removeChild(this.element_);
        container.appendChild(this.element_);
        this.container_ = container;
        // Create outline for the menu (shadow and background).
        var outline = document.createElement('div');
        outline.classList.add(this.CssClasses_.OUTLINE);
        this.outline_ = outline;
        container.insertBefore(outline, this.element_);
        // Find the "for" element and bind events to it.
        var forElId = this.element_.getAttribute('for') || this.element_.getAttribute('data-mdl-for');
        var forEl = null;
        if (forElId) {
            forEl = document.getElementById(forElId);
            if (forEl) {
                this.forElement_ = forEl;
                forEl.addEventListener('click', this.handleForClick_.bind(this));
                forEl.addEventListener('keydown', this.handleForKeyboardEvent_.bind(this));
            }
        }
        // this is a submenu, if the for-element is defined as opening a submenu
        if (this.forElement_) {
            this.isSubMenu = this.forElement_.classList.contains(this.CssClasses_.SUBMENU);
        }
        if (this.isSubMenu) {
            this.outline_.classList.add(this.CssClasses_.OUTLINE_SUBMENU);
        }
        var items = this.element_.querySelectorAll('.' + this.CssClasses_.ITEM);
        this.boundItemKeydown_ = this.handleItemKeyboardEvent_.bind(this);
        this.boundItemClick_ = this.handleItemClick_.bind(this);
        for (var i = 0; i < items.length; i++) {
            // Add a listener to each menu item.
            items[i].addEventListener('click', this.boundItemClick_);
            // Add a tab index to each menu item.
            items[i].tabIndex = '-1';
            // Add a keyboard listener to each menu item.
            items[i].addEventListener('keydown', this.boundItemKeydown_);
        }
        // Add ripple classes to each item, if the user has enabled ripples.
        if (this.element_.classList.contains(this.CssClasses_.RIPPLE_EFFECT)) {
            this.element_.classList.add(this.CssClasses_.RIPPLE_IGNORE_EVENTS);
            for (i = 0; i < items.length; i++) {
                var item = items[i];
                var rippleContainer = document.createElement('span');
                rippleContainer.classList.add(this.CssClasses_.ITEM_RIPPLE_CONTAINER);
                var ripple = document.createElement('span');
                ripple.classList.add(this.CssClasses_.RIPPLE);
                rippleContainer.appendChild(ripple);
                item.appendChild(rippleContainer);
                item.classList.add(this.CssClasses_.RIPPLE_EFFECT);
            }
        }
        // Copy alignment classes to the container, so the outline can use them.
        if (this.element_.classList.contains(this.CssClasses_.BOTTOM_LEFT)) {
            this.outline_.classList.add(this.CssClasses_.BOTTOM_LEFT);
        }
        if (this.element_.classList.contains(this.CssClasses_.BOTTOM_RIGHT)) {
            this.outline_.classList.add(this.CssClasses_.BOTTOM_RIGHT);
        }
        if (this.element_.classList.contains(this.CssClasses_.TOP_LEFT)) {
            this.outline_.classList.add(this.CssClasses_.TOP_LEFT);
        }
        if (this.element_.classList.contains(this.CssClasses_.TOP_RIGHT)) {
            this.outline_.classList.add(this.CssClasses_.TOP_RIGHT);
        }
        if (this.element_.classList.contains(this.CssClasses_.UNALIGNED)) {
            this.outline_.classList.add(this.CssClasses_.UNALIGNED);
        }
        if (this.element_.classList.contains(this.CssClasses_.MOVE_TO_BODY)) {
            // Move the menu-container to the end of the document
            document.body.appendChild(container);
        }
        container.classList.add(this.CssClasses_.IS_UPGRADED);
    }
};
MaterialCascadingMenu.prototype.findAncestor = function (el, cls) {
    while ((el = el.parentElement) && !el.classList.contains(cls));
    return el;
};
/**
     * Handles a click on the "for" element, by positioning the menu and then
     * toggling it.
     *
     * @param {Event} evt The event that fired.
     * @private
     */
MaterialCascadingMenu.prototype.handleForClick_ = function (evt) {
    if (this.element_ && this.forElement_) {
        var rect = this.forElement_.getBoundingClientRect();
        var forRect = this.forElement_.parentElement.getBoundingClientRect();
        var top = 0;
        if (this.forElement_ && this.forElement_.classList.contains(this.CssClasses_.SUBMENU)) {
            // Get the container of the main menu
            const menuMain = this.findAncestor(this.forElement_, 'mdl-menu');
            const containerMain = menuMain.MaterialCascadingMenu.container_;
            if (this.element_.classList.contains(this.CssClasses_.TOP_LEFT)) {
                this.container_.style.left = containerMain.offsetLeft - this.element_.offsetWidth + 'px';
                top = containerMain.offsetTop + this.forElement_.offsetTop - this.element_.offsetHeight + this.forElement_.offsetHeight;
            } else if (this.element_.classList.contains(this.CssClasses_.TOP_RIGHT)) {
                this.container_.style.left = containerMain.offsetLeft + containerMain.offsetWidth + 'px';
                top = containerMain.offsetTop + this.forElement_.offsetTop - this.element_.offsetHeight + this.forElement_.offsetHeight;
            } else if (this.element_.classList.contains(this.CssClasses_.BOTTOM_LEFT)) {
                this.container_.style.left = containerMain.offsetLeft - this.element_.offsetWidth + 'px';
                top = containerMain.offsetTop + this.forElement_.offsetTop;
            } else {
                // Default-behavior ist opening to the right and downwards, since the default of the main menu is also belo and to the right
                this.container_.style.left = containerMain.offsetLeft + containerMain.offsetWidth + 'px';
                top = containerMain.offsetTop + this.forElement_.offsetTop - menuMain.scrollTop;
            }
            // top setzen
            this.container_.style.top = top + 'px';
            // Mit gesetztem Top wird die position des sub-menu ermittelt
            const rect2 = this.element_.getBoundingClientRect();
            const maxHeight = document.documentElement.clientHeight;
            if (rect2.top + rect2.height > maxHeight && this.element_.classList.contains(this.CssClasses_.MAX_HEIGHT) && maxHeight - rect2.top < 150) {
                // falls sub-menu nicht mindestens 150px hoch ist und es ein scroll-balken kriegt, wird es noch nach oben verschoben
                const diff = 150 - (maxHeight - rect2.top);
                this.container_.style.top = top - diff + 'px';
            }
        } else {
            if (this.element_.classList.contains(this.CssClasses_.MOVE_TO_BODY)) {
                // Calculate positions relative to the Viewport (Window)
                const forElementBoundingRect = this.forElement_.getBoundingClientRect();
                var positionBelowFor = forElementBoundingRect.bottom;
                var positionAboveFor = window.innerHeight - forElementBoundingRect.top;
                var positionLeftToFor = forElementBoundingRect.left;
                var positionRightToFor = window.innerWidth - forElementBoundingRect.right;
            } else {
                // Calculate positions relative to containing element of the for-Element
                var positionBelowFor = this.forElement_.offsetTop + this.forElement_.offsetHeight;
                var positionAboveFor = forRect.bottom - rect.top;
                var positionLeftToFor = this.forElement_.offsetLeft;
                var positionRightToFor = forRect.right - rect.right;
            }
            if (this.element_.classList.contains(this.CssClasses_.UNALIGNED)) {
            } else if (this.element_.classList.contains(this.CssClasses_.BOTTOM_RIGHT)) {
                // Position below the "for" element, aligned to its right.
                this.container_.style.right = positionRightToFor + 'px';
                this.container_.style.top = positionBelowFor + 'px';
            } else if (this.element_.classList.contains(this.CssClasses_.TOP_LEFT)) {
                // Position above the "for" element, aligned to its left.
                this.container_.style.left = positionLeftToFor + 'px';
                this.container_.style.bottom = positionAboveFor + 'px';
            } else if (this.element_.classList.contains(this.CssClasses_.TOP_RIGHT)) {
                // Position above the "for" element, aligned to its right.
                this.container_.style.right = positionRightToFor + 'px';
                this.container_.style.bottom = positionAboveFor + 'px';
            } else {
                // Default: position below the "for" element, aligned to its left.
                this.container_.style.left = positionLeftToFor + 'px';
                this.container_.style.top = positionBelowFor + 'px';
            }
        }
    }
    this.toggle(evt);
};
/**
     * Handles a keyboard event on the "for" element.
     *
     * @param {Event} evt The event that fired.
     * @private
     */
MaterialCascadingMenu.prototype.handleForKeyboardEvent_ = function (evt) {
    if (this.element_ && this.container_ && this.forElement_) {
        var items = this.element_.querySelectorAll('.' + this.CssClasses_.ITEM + ':not([disabled])');
        if (items && items.length > 0 && this.container_.classList.contains(this.CssClasses_.IS_VISIBLE)) {
            if (evt.keyCode === this.Keycodes_.UP_ARROW) {
                evt.preventDefault();
                items[items.length - 1].focus();
            } else if (evt.keyCode === this.Keycodes_.DOWN_ARROW) {
                evt.preventDefault();
                items[0].focus();
            }
        }
    }
};
/**
     * Handles a keyboard event on an item.
     *
     * @param {Event} evt The event that fired.
     * @private
     */
MaterialCascadingMenu.prototype.handleItemKeyboardEvent_ = function (evt) {
    if (this.element_ && this.container_) {
        var items = this.element_.querySelectorAll('.' + this.CssClasses_.ITEM + ':not([disabled])');
        if (items && items.length > 0 && this.container_.classList.contains(this.CssClasses_.IS_VISIBLE)) {
            var currentIndex = Array.prototype.slice.call(items).indexOf(evt.target);
            if (evt.keyCode === this.Keycodes_.UP_ARROW) {
                evt.preventDefault();
                if (currentIndex > 0) {
                    items[currentIndex - 1].focus();
                } else {
                    items[items.length - 1].focus();
                }
            } else if (evt.keyCode === this.Keycodes_.DOWN_ARROW) {
                evt.preventDefault();
                if (items.length > currentIndex + 1) {
                    items[currentIndex + 1].focus();
                } else {
                    items[0].focus();
                }
            } else if (evt.keyCode === this.Keycodes_.SPACE || evt.keyCode === this.Keycodes_.ENTER) {
                evt.preventDefault();
                // Send mousedown and mouseup to trigger ripple.
                var e = new MouseEvent('mousedown');
                evt.target.dispatchEvent(e);
                e = new MouseEvent('mouseup');
                evt.target.dispatchEvent(e);
                // Send click.
                evt.target.click();
            } else if (evt.keyCode === this.Keycodes_.ESCAPE) {
                evt.preventDefault();
                this.hide();
            }
        }
    }
};
/**
     * Handles a click event on an item.
     *
     * @param {Event} evt The event that fired.
     * @private
     */
MaterialCascadingMenu.prototype.handleItemClick_ = function (evt) {
    if (evt.target.hasAttribute('disabled') || evt.target.classList.contains(this.CssClasses_.NOCLOSE)) {
        evt.stopPropagation();
    } else {
        if (evt.target.classList.contains(this.CssClasses_.SUBMENU)) {
            evt.stopPropagation();
        } else {
            // Wait some time before closing menu, so the user can see the ripple.
            this.closing_ = true;
            window.setTimeout(function (evt) {
                this.hide();
                this.closing_ = false;
            }.bind(this), this.Constant_.CLOSE_TIMEOUT);
        }
    }
};
/**
     * Calculates the initial clip (for opening the menu) or final clip (for closing
     * it), and applies it. This allows us to animate from or to the correct point,
     * that is, the point it's aligned to in the "for" element.
     *
     * @param {number} height Height of the clip rectangle
     * @param {number} width Width of the clip rectangle
     * @private
     */
MaterialCascadingMenu.prototype.applyClip_ = function (height, width) {
    if (this.element_.classList.contains(this.CssClasses_.UNALIGNED)) {
        // Do not clip.
        this.element_.style.clip = '';
    } else if (this.element_.classList.contains(this.CssClasses_.BOTTOM_RIGHT)) {
        // Clip to the top right corner of the menu.
        this.element_.style.clip = 'rect(0 ' + width + 'px ' + '0 ' + width + 'px)';
    } else if (this.element_.classList.contains(this.CssClasses_.TOP_LEFT)) {
        // Clip to the bottom left corner of the menu.
        this.element_.style.clip = 'rect(' + height + 'px 0 ' + height + 'px 0)';
    } else if (this.element_.classList.contains(this.CssClasses_.TOP_RIGHT)) {
        // Clip to the bottom right corner of the menu.
        this.element_.style.clip = 'rect(' + height + 'px ' + width + 'px ' + height + 'px ' + width + 'px)';
    } else {
        // Default: do not clip (same as clipping to the top left corner).
        this.element_.style.clip = '';
    }
};
/**
     * Cleanup function to remove animation listeners.
     *
     * @param {Event} evt
     * @private
     */
MaterialCascadingMenu.prototype.removeAnimationEndListener_ = function (evt) {
    evt.target.classList.remove(MaterialCascadingMenu.prototype.CssClasses_.IS_ANIMATING);
};
/**
     * Adds an event listener to clean up after the animation ends.
     *
     * @private
     */
MaterialCascadingMenu.prototype.addAnimationEndListener_ = function () {
    this.element_.addEventListener('transitionend', this.removeAnimationEndListener_);
    this.element_.addEventListener('webkitTransitionEnd', this.removeAnimationEndListener_);
};
/**
     * Displays the menu.
     *
     * @public
     */
MaterialCascadingMenu.prototype.show = function (evt) {
    if (this.element_ && this.container_ && this.outline_) {
        // Measure the inner element.
        const rect = this.element_.getBoundingClientRect();
        const maxHeight = document.documentElement.clientHeight;
        // Measure the inner element.
        var height = rect.height;
        var width = rect.width;
        // Hier wird noch abgefragt ob das Menü grösser als das Fenster ist, falls ja wird ein Scrollbalken eingesetzt
        if (rect.top + rect.height > maxHeight && this.element_.classList.contains(this.CssClasses_.MAX_HEIGHT)) {
            height = document.documentElement.clientHeight - rect.top - 20;
            this.element_.style.overflow = 'auto';
            this.element_.style.height = '100%';
            this.element_.style.width = '100%';
            this.element_.style.padding = '0';
        }
        // Falls das Menü links über den Rand läuft, wird die Breite eingegrenzt (rechts braucht man das nicht zu tun, da die Breite des Dokument automatisch angepasst wird und man nach rechts scrollen kann)
        if (rect.left < 0 && (this.element_.classList.contains(this.CssClasses_.TOP_RIGHT) || this.element_.classList.contains(this.CssClasses_.BOTTOM_RIGHT))) {
            width += rect.left;
        }
        // Apply the inner element's size to the container and outline.
        this.container_.style.width = width + 'px';
        this.container_.style.height = height + 'px';
        this.outline_.style.width = width + 'px';
        this.outline_.style.height = height + 'px';
        var transitionDuration = this.Constant_.TRANSITION_DURATION_SECONDS * this.Constant_.TRANSITION_DURATION_FRACTION;
        // Calculate transition delays for individual menu items, so that they fade
        // in one at a time.
        var items = this.element_.querySelectorAll('.' + this.CssClasses_.ITEM);
        for (var i = 0; i < items.length; i++) {
            var itemDelay = null;
            if (this.element_.classList.contains(this.CssClasses_.TOP_LEFT) || this.element_.classList.contains(this.CssClasses_.TOP_RIGHT)) {
                itemDelay = (height - items[i].offsetTop - items[i].offsetHeight) / height * transitionDuration + 's';
            } else {
                itemDelay = items[i].offsetTop / height * transitionDuration + 's';
            }
            items[i].style.transitionDelay = itemDelay;
        }
        // Apply the initial clip to the text before we start animating.
        this.applyClip_(height, width);
        // Wait for the next frame, turn on animation, and apply the final clip.
        // Also make it visible. This triggers the transitions.
        window.requestAnimationFrame(function () {
            this.element_.classList.add(this.CssClasses_.IS_ANIMATING);
            this.element_.style.clip = 'rect(0 ' + width + 'px ' + height + 'px 0)';
            this.container_.classList.add(this.CssClasses_.IS_VISIBLE);
        }.bind(this));
        // Clean up after the animation is complete.
        this.addAnimationEndListener_();
        // Add a click listener to the document, to close the menu.
        var callback = function (e) {
            // Check to see if the document is processing the same event that
            // displayed the menu in the first place. If so, do nothing.
            // Also check to see if the menu is in the process of closing itself, and
            // do nothing in that case.
            // Also check if the clicked element is a menu item
            // if so, do nothing.
            if (e !== evt && !this.closing_ && e.target.parentNode !== this.element_) {
                document.removeEventListener('click', callback);
                this.hide();
            }
        }.bind(this);
        document.addEventListener('click', callback);
    }
};
MaterialCascadingMenu.prototype['show'] = MaterialCascadingMenu.prototype.show;
/**
     * Hides the menu.
     *
     * @public
     */
MaterialCascadingMenu.prototype.hide = function () {
    if (this.element_ && this.container_ && this.outline_) {
        var items = this.element_.querySelectorAll('.' + this.CssClasses_.ITEM);
        // Remove all transition delays; menu items fade out concurrently.
        for (var i = 0; i < items.length; i++) {
            items[i].style.removeProperty('transition-delay');
        }
        // Measure the inner element.
        var rect = this.element_.getBoundingClientRect();
        var height = rect.height;
        var width = rect.width;
        // Turn on animation, and apply the final clip. Also make invisible.
        // This triggers the transitions.
        this.element_.classList.add(this.CssClasses_.IS_ANIMATING);
        this.applyClip_(height, width);
        this.container_.classList.remove(this.CssClasses_.IS_VISIBLE);
        // Clean up after the animation is complete.
        this.addAnimationEndListener_();
    }
};
MaterialCascadingMenu.prototype['hide'] = MaterialCascadingMenu.prototype.hide;
/**
     * Displays or hides the menu, depending on current state.
     *
     * @public
     */
MaterialCascadingMenu.prototype.toggle = function (evt) {
    if (this.container_.classList.contains(this.CssClasses_.IS_VISIBLE)) {
        this.hide();
    } else {
        this.show(evt);
    }
};
MaterialCascadingMenu.prototype['toggle'] = MaterialCascadingMenu.prototype.toggle;
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: MaterialCascadingMenu,
    classAsString: 'MaterialCascadingMenu',
    cssClass: 'mdl-js-cascading-menu',
    widget: true
});